import React from 'react';
import { ArrowRight } from 'lucide-react';
import { CATEGORY_LABELS } from '../../data/productsData';

// Highlights `query` matches inside `text` with a soft emerald mark.
export const HighlightText = ({ text, query }) => {
  if (!query || query.trim().length < 2) return <>{text}</>;
  const safe = query.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${safe})`, 'ig'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.trim().toLowerCase() ? (
          <mark key={i} className="bg-emerald-100 text-emerald-800 rounded px-0.5">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

const CATEGORY_BADGE_STYLES = {
  termite: 'bg-amber-50 text-amber-700 border-amber-200',
  cockroach: 'bg-rose-50 text-rose-700 border-rose-200',
  mosquito: 'bg-sky-50 text-sky-700 border-sky-200',
  rodent: 'bg-slate-100 text-slate-700 border-slate-200',
  bedbug: 'bg-violet-50 text-violet-700 border-violet-200',
  ant: 'bg-orange-50 text-orange-700 border-orange-200',
  fly: 'bg-teal-50 text-teal-700 border-teal-200',
  general: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export const ProductCard = ({ product, query, onLearnMore, index }) => (
  <article
    className="product-card group relative bg-white/80 backdrop-blur-sm border border-slate-200/80 rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(15,40,30,0.06)] hover:shadow-[0_16px_40px_rgba(6,78,59,0.16)] hover:-translate-y-1.5 hover:border-emerald-200 transition-[transform,box-shadow,border-color] duration-300 flex flex-col"
    style={{ animationDelay: `${Math.min(index * 60, 480)}ms` }}
    data-testid={`product-card-${product.id}`}
  >
    <div className="relative h-48 overflow-hidden bg-slate-100">
      <img
        src={product.image}
        alt={product.name}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 saturate-[0.85] group-hover:saturate-100"
      />
      {/* Dim layer to signal coming-soon */}
      <div className="absolute inset-0 bg-slate-900/30" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent opacity-100 transition-opacity duration-300" />
      <span
        className={`absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border backdrop-blur-sm ${CATEGORY_BADGE_STYLES[product.category] || CATEGORY_BADGE_STYLES.general}`}
        data-testid={`product-category-badge-${product.id}`}
      >
        {CATEGORY_LABELS[product.category]}
      </span>
      {/* COMING SOON badge */}
      <div className="absolute top-3 right-3 z-10" data-testid={`product-coming-soon-${product.id}`}>
        <div className="bg-gradient-to-r from-amber-400 to-amber-500 text-amber-950 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md ring-2 ring-white/40 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-900 animate-pulse" />
          Coming Soon
        </div>
      </div>
      {/* Center watermark — bigger, more prominent */}
      <div className="absolute inset-x-0 bottom-3 flex justify-center pointer-events-none">
        <div className="bg-white/95 backdrop-blur-sm text-slate-900 text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-sm">
          🚀 Launching Soon
        </div>
      </div>
    </div>

    <div className="p-5 flex flex-col flex-1">
      <h3 className="text-base font-bold text-slate-900 leading-snug" data-testid={`product-name-${product.id}`}>
        <HighlightText text={product.name} query={query} />
      </h3>
      <p className="mt-2 text-sm text-slate-500 leading-relaxed line-clamp-3 flex-1">
        <HighlightText text={product.shortDescription} query={query} />
      </p>
      <button
        type="button"
        onClick={() => onLearnMore(product)}
        className="mt-4 inline-flex items-center justify-center gap-2 w-full h-10 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-emerald-600 active:scale-[0.98] transition-[background-color,transform] duration-200"
        data-testid={`product-learn-more-${product.id}`}
      >
        Learn More
        <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
      </button>
    </div>
  </article>
);

export const ProductCardSkeleton = () => (
  <div className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden" aria-hidden>
    <div className="h-48 skeleton-shimmer" />
    <div className="p-5 space-y-3">
      <div className="h-4 w-3/4 rounded skeleton-shimmer" />
      <div className="h-3 w-full rounded skeleton-shimmer" />
      <div className="h-3 w-5/6 rounded skeleton-shimmer" />
      <div className="h-10 w-full rounded-full skeleton-shimmer" />
    </div>
  </div>
);
